import React, { useEffect, useState } from 'react';
import { Compass, ArrowRight } from 'lucide-react';
import advisor from '../lib/advisor';
import AdvisorExplanation from './AdvisorExplanation';

/**
 * Tarjeta Habita Advisor — MVP visual.
 *
 * Reglas:
 *   - Fire-and-forget: si el motor no responde en ~2s, no se muestra nada.
 *   - Silenciosa: nunca deja placeholders "cargando…" prolongados.
 *   - Sin score numérico visible al usuario. Solo tono cualitativo.
 *   - Registra recommendation_shown al montar.
 */
export default function AdvisorCard({ propertyId, onContactClick }) {
  const [rec, setRec] = useState(null);
  const [targetItem, setTargetItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await advisor.recommend({ limit: 5 });
      if (cancelled || !r || !r.items?.length) return;
      // Intenta hacer match con la propiedad actualmente vista
      const match = r.items.find(i => i.item_id === propertyId) || r.items[0];
      setRec(r);
      setTargetItem(match);
      advisor.recordRecommendationEvent(r.id, 'shown', { property_id: propertyId });
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  if (dismissed || !rec || !targetItem) return null;

  const isForThisProperty = targetItem.item_id === propertyId;
  const headline = isForThisProperty
    ? targetItem.explanation?.human_readable?.split('.')[0] + '.'
    : `Encontramos una propiedad que podría interesarte más${targetItem.snapshot?.city ? ` en ${targetItem.snapshot.city}` : ''}.`;

  const openExplanation = () => {
    advisor.recordRecommendationEvent(rec.id, 'explanation_opened', { property_id: propertyId });
    setShowModal(true);
  };

  const dismiss = () => {
    advisor.recordRecommendationEvent(rec.id, 'dismissed', { property_id: propertyId });
    setDismissed(true);
  };

  return (
    <>
      <div
        data-testid="advisor-card"
        className="relative overflow-hidden rounded-2xl border border-habita/30 bg-gradient-to-br from-habita-mint/60 via-white to-white shadow-sm"
      >
        <button
          onClick={dismiss}
          data-testid="advisor-card-dismiss"
          className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-lg leading-none"
          aria-label="Cerrar recomendación"
        >×</button>
        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-full bg-habita text-white grid place-items-center">
              <Compass className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-habita-dark">
              Habita Advisor
            </span>
          </div>
          <p className="text-sm sm:text-[15px] text-habita-dark leading-relaxed pr-4">
            {headline}
          </p>
          <button
            onClick={openExplanation}
            data-testid="advisor-card-explain"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-habita hover:text-habita-dark transition-colors"
          >
            ¿Por qué? <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showModal && (
        <AdvisorExplanation
          recommendation={rec}
          item={targetItem}
          isSameProperty={isForThisProperty}
          onClose={() => setShowModal(false)}
          onNavigate={() => {
            advisor.recordRecommendationEvent(rec.id, 'clicked', { property_id: targetItem.item_id });
          }}
          onContact={() => {
            advisor.recordRecommendationEvent(rec.id, 'contacted', { property_id: targetItem.item_id });
            onContactClick?.();
          }}
        />
      )}
    </>
  );
}
